import pdfMake from 'addthaifont-pdfmake';
import 'addthaifont-pdfmake/build/vfs_fonts';
import dayjs from 'dayjs';

pdfMake.fonts = {
  Sarabun: {
    normal: 'Sarabun-Light.ttf',
    bold: 'Sarabun-Regular.ttf',
    italics: 'Sarabun-LightItalic.ttf',
    bolditalics: 'Sarabun-Italic.ttf',
  },
};

const LEAVE_ABBR = {
  SICK: 'ป่วย',
  PERSONAL: 'กิจ',
  VACATION: 'พักร้อน',
  MATERNITY: 'คลอด',
  ORDINATION: 'บวช',
  UNPAID: 'ไม่จ้าง',
};
const LEAVE_BG = '#fce7f3';
const LEAVE_TEXT = '#be185d';

// สร้าง map: empId → { 'YYYY-MM-DD': leaveRecord }
const buildLeaveMap = (leaveData) => {
  const map = {};
  (leaveData || []).forEach((lr) => {
    const empId = String(lr.employee?._id || lr.employee || '');
    if (!empId) return;
    if (!map[empId]) map[empId] = {};
    let d = dayjs(lr.start_date).startOf('day');
    const lEnd = dayjs(lr.end_date).startOf('day');
    while (!d.isAfter(lEnd)) {
      map[empId][d.format('YYYY-MM-DD')] = lr;
      d = d.add(1, 'day');
    }
  });
  return map;
};

const timeStampMontlyReport = (
  info,
  timestamp,
  days,
  month,
  year,
  dateStart,
  leaveData = [],
  liveSalaryData = [],
  liveProjectDashboard = [],
) => {
  const leaveMap = buildLeaveMap(leaveData);

  // ยอดค่าแรง/OT ที่คำนวณถูกต้องแล้วจากตารางเงินเดือน (รองรับ FULLTIME spd×30, override, วันลา ฯลฯ)
  // ใช้แทนค่า totalAmount/totalOtAmount ดิบจาก backend ที่ยังไม่ผ่านสูตรพวกนี้เลย
  const liveSalaryMap = {};
  (liveSalaryData || []).forEach((row) => {
    if (row?.employee_id != null) liveSalaryMap[String(row.employee_id)] = row;
  });

  const getLeave = (empId, day) => {
    const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return leaveMap[String(empId)]?.[key] || null;
  };

  // นับวันลา (อนุมัติ) ที่ไม่มี check-in — สำหรับ FULLTIME ให้บวกเป็นวันทำงาน
  const countApprovedLeaveDays = (empId, checkins) => {
    const empLeaves = leaveMap[String(empId)] || {};
    let extra = 0;
    Object.entries(empLeaves).forEach(([dateKey, lr]) => {
      const d = dayjs(dateKey);
      if (d.year() !== year || d.month() !== month) return;
      const hasCheckin = (checkins || []).some(
        (c) => dayjs(c.checkIn).format('YYYY-MM-DD') === dateKey,
      );
      if (!hasCheckin) {
        extra += lr.period === 'FULL_DAY' ? 1 : 0.5;
      }
    });
    return extra;
  };

  // เซลล์แถว "ปกติ" — รวม leave info, leaveAutoH = ชั่วโมงลา auto (0/4/8)
  const makeNormalCell = (foundData, leave, leaveAutoH = 0) => {
    if (!leave) {
      return {
        text: (foundData?.totalHour * foundData?.rate || 1).toFixed(1),
        alignment: 'center',
        color: foundData?.totalHour ? '#1976d2' : '#bbb',
        margin: [0, 2, 0, 2],
        fontSize: 6,
      };
    }

    const abbr = LEAVE_ABBR[leave.leave_type] || 'ลา';
    const isHalf = leave.period !== 'FULL_DAY';
    const nonPeriod = leave.period === 'MORNING' ? 'บ่าย' : 'เช้า';

    if (!isHalf) {
      // ลาเต็มวัน — แสดง "ป่วย/8h" สำหรับ FULLTIME
      return {
        text: leaveAutoH > 0 ? `${abbr} ${leaveAutoH}h` : abbr,
        alignment: 'center',
        fillColor: LEAVE_BG,
        color: LEAVE_TEXT,
        bold: true,
        fontSize: 6,
        margin: [0, 2, 0, 2],
      };
    }

    // ลาครึ่งวัน
    const checkinHour = foundData ? dayjs(foundData.checkIn).hour() : null;
    const inLeavePeriod = foundData != null
      ? (leave.period === 'MORNING' ? checkinHour < 12 : checkinHour >= 12)
      : null;
    const statusColor = !foundData ? '#9ca3af' : inLeavePeriod ? '#dc2626' : '#16a34a';
    const statusIcon = !foundData ? '✗' : inLeavePeriod ? '⚠' : '✓';
    const actualH = foundData ? foundData.totalHour * (foundData.rate || 1) : 0;
    // รวมชั่วโมงทำงานจริง + ชั่วโมงลา auto
    const combinedH = actualH + leaveAutoH;
    const hoursText = combinedH > 0 ? combinedH.toFixed(1) : '';

    return {
      stack: [
        {
          text: hoursText || abbr,
          fontSize: 6,
          color: combinedH > 0 ? '#1976d2' : LEAVE_TEXT,
          bold: combinedH === 0,
          alignment: 'center',
        },
        {
          text: `½${abbr} ${nonPeriod}${statusIcon}`,
          fontSize: 5,
          color: statusColor,
          alignment: 'center',
        },
      ],
      fillColor: LEAVE_BG,
      margin: [0, 1, 0, 1],
    };
  };

  // เซลล์แถว "OT" — แสดง OT ตามปกติ ถ้าลาเต็มวัน + ไม่มี checkin → pink
  const makeOtCell = (foundData, leave) => {
    const isFullLeave = leave && leave.period === 'FULL_DAY' && !foundData;
    return {
      text: foundData?.otRequests?.total_hours?.toFixed(1) || '',
      alignment: 'center',
      color: foundData?.otRequests?.total_hours ? '#d32f2f' : '#bbb',
      margin: [0, 2, 0, 2],
      fontSize: 6,
      ...(isFullLeave ? { fillColor: LEAVE_BG } : {}),
    };
  };

  const tableBody = [
    [
      { text: 'No.', style: 'tableHeader', fillColor: '#d2d4d2', alignment: 'center' },
      { text: 'Name', style: 'tableHeader', fillColor: '#d2d4d2', alignment: 'left' },
      { text: 'Type', style: 'tableHeader', fillColor: '#d2d4d2', alignment: 'center' },
      ...days.map((day) => {
        const date = dayjs(`${year}-${month + 1}-${day}`);
        const isWeekend = date.day() === 0 || date.day() === 6;
        return {
          text: day.toString(),
          style: 'tableHeader',
          fillColor: isWeekend ? '#ffb3b3' : '#e6e6e6',
          alignment: 'center',
          fontSize: 6,
        };
      }),
      { text: 'Working', style: 'tableHeader', fillColor: '#d2d4d2', alignment: 'center' },
      { text: 'Day Rate', style: 'tableHeader', fillColor: '#d2d4d2', alignment: 'right' },
      { text: 'ค่าแรง', style: 'tableHeader', fillColor: '#d2d4d2', alignment: 'right' },
      { text: 'OT', style: 'tableHeader', fillColor: '#d2d4d2', alignment: 'right' },
      { text: 'Amount', style: 'tableHeader', fillColor: '#d2d4d2', alignment: 'right' },
    ],
  ];

  timestamp?.rows?.forEach((employee, idx) => {
    const empId = String(employee.employee_id || '');
    const leaveDays = employee.emp_type === 'FULLTIME'
      ? countApprovedLeaveDays(empId, employee.checkins)
      : 0;
    const adjustedWork = (employee.totalWork || 0) + leaveDays;

    // ใช้ยอดสดจากตารางเงินเดือนถ้ามี (ตรงกับหน้าจอเป๊ะ) ไม่มีค่อย fallback ไปยอดดิบจาก backend
    const liveRow = liveSalaryMap[empId];
    const liveAmount = liveRow?.revenue?.salary != null ? parseFloat(liveRow.revenue.salary) : null;
    const liveOtAmount = liveRow?.revenue?.overtime != null ? parseFloat(liveRow.revenue.overtime) : null;
    const displayAmount = liveAmount != null && !isNaN(liveAmount) ? liveAmount : (employee?.totalAmount || 0);
    const displayOtAmount = liveOtAmount != null && !isNaN(liveOtAmount) ? liveOtAmount : (employee?.totalOtAmount || 0);

    // แถว "ปกติ"
    tableBody.push([
      { text: idx + 1, rowSpan: 2, alignment: 'center', style: 'tableBody', margin: [0, 2, 0, 2] },
      { text: `${employee?.name}`, rowSpan: 2, alignment: 'left', style: 'tableBody', margin: [1, 2, 0, 2] },
      { text: 'ปกติ', alignment: 'center', style: 'tableBody', color: '#333' },
      ...days.map((day) => {
        const foundData = employee.checkins?.find(
          (e) => dayjs(e?.checkIn).format('D/M/YY') === `${day}/${month + 1}/${year % 100}`,
        );
        const leave = getLeave(empId, day);
        const checkinHour = foundData ? dayjs(foundData.checkIn).hour() : null;
        const leaveAutoH = (() => {
          if (!leave || employee.emp_type !== 'FULLTIME') return 0;
          if (leave.period === 'FULL_DAY' && !foundData) return 8;
          if (leave.period === 'MORNING' && (!foundData || checkinHour >= 12)) return 4;
          if (leave.period === 'AFTERNOON' && (!foundData || checkinHour < 12)) return 4;
          return 0;
        })();
        return makeNormalCell(foundData, leave, leaveAutoH);
      }),
      { text: adjustedWork.toFixed(1), alignment: 'center', style: 'tableBody', color: '#1976d2', margin: [0, 2, 0, 2] },
      { text: employee?.salary_per_day?.toFixed(2) || '-', rowSpan: 2, style: 'tableBody', alignment: 'right', margin: [0, 4, 0, 4] },
      { text: displayAmount.toFixed(2), rowSpan: 2, style: 'tableBody', alignment: 'right', margin: [0, 4, 0, 4] },
      { text: displayOtAmount.toFixed(2), rowSpan: 2, style: 'tableBody', alignment: 'right', margin: [0, 2, 1, 2], color: '#d32f2f' },
      { text: (displayAmount + displayOtAmount).toFixed(2), style: 'tableBody', alignment: 'right', rowSpan: 2, margin: [0, 2, 1, 2], bold: true },
    ]);

    // แถว "OT"
    tableBody.push([
      {}, // No.
      {}, // Name
      { text: 'OT', alignment: 'center', style: 'tableBody', color: '#d32f2f' },
      ...days.map((day) => {
        const foundData = employee.checkins?.find(
          (e) => dayjs(e?.checkIn).format('D/M/YY') === `${day}/${month + 1}/${year % 100}`,
        );
        const leave = getLeave(empId, day);
        return makeOtCell(foundData, leave);
      }),
      { text: employee?.totalOtHour?.toFixed(1) || '-', alignment: 'center', style: 'tableBody', color: '#d32f2f', margin: [0, 2, 0, 2] },
      {}, // Day Rate
      {}, // ค่าแรง
      {}, // OT
      {}, // Amount
    ]);
  });

  // --- ตารางสรุปค่าแรงตามโปรเจค ---
  const projectCostTableBody = [
    [
      { text: 'โครงการ', style: 'tableHeader', fillColor: '#d2d4d2' },
      { text: 'ค่าแรง', style: 'tableHeader', fillColor: '#d2d4d2' },
      { text: 'OT', style: 'tableHeader', fillColor: '#d2d4d2' },
      { text: 'รวม', style: 'tableHeader', fillColor: '#d2d4d2' },
    ],
  ];

  // ใช้ projectDashboard ที่คำนวณถูกแล้วจากตารางเงินเดือน (group by project จาก checkin จริง) ถ้ามี
  // ไม่งั้น fallback ไปยอดดิบจาก backend (timestamp.dashboard) — กันกรณีเรียกจากที่อื่นที่ยังไม่ได้ส่งมาให้
  const dashboardRows = (liveProjectDashboard && liveProjectDashboard.length > 0)
    ? liveProjectDashboard
    : (timestamp?.dashboard || []);

  if (dashboardRows.length > 0) {
    dashboardRows.forEach((row) => {
      projectCostTableBody.push([
        row?.project_number || '-',
        { text: (row?.total_salary ?? 0).toLocaleString('th-TH', { minimumFractionDigits: 2 }), alignment: 'right' },
        { text: (row?.total_ot ?? 0).toLocaleString('th-TH', { minimumFractionDigits: 2 }), alignment: 'right' },
        { text: ((row?.total_salary ?? 0) + (row?.total_ot ?? 0)).toLocaleString('th-TH', { minimumFractionDigits: 2 }), alignment: 'right' },
      ]);
    });
    projectCostTableBody.push([
      { text: 'รวมทั้งหมด', style: 'tableHeader', alignment: 'center' },
      { text: dashboardRows.reduce((s, r) => s + (r?.total_salary || 0), 0).toLocaleString('th-TH', { minimumFractionDigits: 2 }), alignment: 'right', style: 'tableHeader' },
      { text: dashboardRows.reduce((s, r) => s + (r?.total_ot || 0), 0).toLocaleString('th-TH', { minimumFractionDigits: 2 }), alignment: 'right', style: 'tableHeader' },
      { text: dashboardRows.reduce((s, r) => s + ((r?.total_salary || 0) + (r?.total_ot || 0)), 0).toLocaleString('th-TH', { minimumFractionDigits: 2 }), alignment: 'right', style: 'tableHeader' },
    ]);
  } else {
    projectCostTableBody.push([
      { text: 'ไม่มีข้อมูล', colSpan: 4, alignment: 'center', color: '#888' },
      {}, {}, {},
    ]);
  }

  // สร้าง legend สำหรับอธิบายสัญลักษณ์การลา
  const legendContent = leaveData.length > 0 ? [
    {
      text: 'หมายเหตุ: ',
      bold: true,
      fontSize: 8,
      margin: [0, 8, 0, 2],
    },
    {
      columns: [
        { text: '■ ป่วย/กิจ/พักร้อน = ประเภทการลา', fontSize: 7, color: LEAVE_TEXT, width: 'auto' },
        { text: '  ½ = ลาครึ่งวัน', fontSize: 7, width: 'auto' },
        { text: '  ✓ = ลงเวลาช่วงที่ไม่ลา', fontSize: 7, color: '#16a34a', width: 'auto' },
        { text: '  ⚠ = ลงเวลาในช่วงลา', fontSize: 7, color: '#dc2626', width: 'auto' },
        { text: '  ✗ = ไม่มีการลงเวลาช่วงที่ไม่ลา', fontSize: 7, color: '#9ca3af', width: 'auto' },
      ],
      columnGap: 8,
    },
  ] : [];

  const docDefinition = {
    pageSize: 'A4',
    pageOrientation: 'landscape',
    pageMargins: [15, 80, 15, 60],
    defaultStyle: {
      font: 'Sarabun',
      fontSize: 7,
    },
    info: {
      title: 'Employee Time Sheet',
      author: 'MNS System',
      subject: `Time Sheet ${dayjs(dateStart).format('MMMM YYYY')}`,
    },
    header(currentPage, pageCount, pageSize) {
      return [
        {
          columns: [
            { text: info?.name, style: 'header', alignment: 'left', margin: [20, 10, 0, 0] },
            { text: 'Employee Time Sheet', style: 'header', alignment: 'center', margin: [0, 10, 0, 0] },
            { text: `วันที่พิมพ์: ${dayjs().format('DD/MM/YYYY HH:mm')}`, alignment: 'right', fontSize: 10, margin: [0, 10, 20, 0] },
          ],
        },
        { text: `เดือน ${dayjs(dateStart).format('MMMM YYYY')}`, alignment: 'center', fontSize: 12, margin: [0, 2, 0, 10] },
        { canvas: [{ type: 'line', x1: 20, y1: 0, x2: pageSize.width - 20, y2: 0, lineWidth: 1, lineColor: '#bbb' }], margin: [0, 0, 0, 10] },
      ];
    },
    content: [
      {
        table: {
          headerRows: 1,
          widths: [18, '*', 25, ...days.map(() => 12), 35, 40, 40, 35, 45],
          body: tableBody,
        },
        layout: {
          hLineWidth(i, node) {
            if (i === 0 || i === node.table.body.length) return 1.5;
            return 0.5;
          },
          vLineWidth() { return 0.7; },
          vLineColor() { return '#bbb'; },
          paddingLeft() { return 1; },
          paddingRight() { return 1; },
          paddingTop() { return 1; },
          paddingBottom() { return 1; },
        },
        margin: [0, 0, 0, 4],
      },
      ...legendContent,
      { text: 'สรุปค่าแรงตามโครงการ (ไม่หักภาษีและประกันสังคม)', style: 'header', fontSize: 13, margin: [0, 10, 0, 6] },
      {
        table: {
          headerRows: 1,
          widths: [70, 80, 80, 90],
          body: projectCostTableBody,
        },
        layout: 'lightHorizontalLines',
      },
    ],
    footer(currentPage, pageCount, pageSize) {
      return [
        { canvas: [{ type: 'line', x1: 20, y1: 0, x2: pageSize.width - 20, y2: 0, lineWidth: 1, lineColor: '#bbb' }], margin: [0, 0, 0, 6] },
        {
          columns: [
            { text: info?.name_eng, alignment: 'left', fontSize: 10, margin: [20, 2, 0, 0] },
            { text: `หน้าที่ ${currentPage} / ${pageCount}`, alignment: 'right', fontSize: 10, margin: [0, 2, 20, 0] },
          ],
        },
      ];
    },
    styles: {
      header: { fontSize: 14, bold: true },
      tableHeader: { bold: true, fillColor: '#CCCCCC', alignment: 'center' },
      tableBody: { alignment: 'right' },
    },
  };

  pdfMake.createPdf(docDefinition).open();
};
export default timeStampMontlyReport;
